import { useEffect, useState } from 'react';
import { Accordion as AccordionsGroup, AccordionTab } from 'primereact/accordion';
import classNames from 'classnames';
import { Button } from '../Button/Button';
import { AccordionSize, AccordionVariant, IAccordion } from './Accordion.types';
import { ButtonStyle, ButtonVariant } from '../Button/Button.types';
import styles from './Accordion.module.scss';

export const Accordion: React.FC<IAccordion> = ({
  tabs,
  tabHeaderPrefix = '',
  activeIndex = 0,
  multiple = false,
  variant = AccordionVariant.Default,
  size = AccordionSize.Small,
  className = '',
  shouldPreserveFirstTab = false,
  isDeleteDisabled = false,
  allowToDelete = false,
  additionalTabConfig,
  highlightActive = true,
  onDeleteTab,
  onTabChange,
  preventTabsToggle = false,
}) => {
  const [activeIndexState, setActiveIndexState] = useState<number | number[] | null>(activeIndex);

  const deleteTab = (index: number) => {
    if (!isDeleteDisabled && onDeleteTab) onDeleteTab(index);
  };

  useEffect(() => {
    if (activeIndex !== undefined) {
      setActiveIndexState(activeIndex);
    }
  }, [activeIndex]);

  useEffect(() => {
    setTimeout(() => {
      const elements = document.querySelectorAll('.p-accordion-header-link');
      elements.forEach((el) => el?.removeAttribute('href'));
    });
  }, [tabs]);

  return (
    <div
      className={classNames(
        className,
        styles['ds-accordion-container'],
        styles[`accordion-${variant}`],
        styles[`accordion-${size}`],
        { [styles['accordion-additional-tab']]: additionalTabConfig, [styles['highlight-active-tab']]: highlightActive }
      )}
      data-testid="accordion-element"
    >
      {additionalTabConfig && (
        <div className={styles['add-another-header']}>
          <h5>{additionalTabConfig.buttonLabel}</h5>
          <Button
            className={additionalTabConfig.disableAdd ? styles['add-another-btn-disabled'] : ''}
            data-testid="add-another-btn"
            variant={ButtonVariant.contained}
            styleType={ButtonStyle.secondary}
            disabled={additionalTabConfig.disableAdd}
            onClick={additionalTabConfig.onAdd}
          >
            <div className={styles['add-another-icon']}></div>
            <p className={styles['button-text']}>ADD ANOTHER</p>
          </Button>
        </div>
      )}
      <AccordionsGroup
        transitionOptions={{ timeout: 0 }}
        expandIcon={
          <span
            className={classNames(styles['accordion-toggle-icon'], styles['accordion-expand-icon'], {
              [styles['highlight-icon']]: highlightActive,
            })}
          ></span>
        }
        collapseIcon={
          <span
            className={classNames(styles['accordion-toggle-icon'], styles['accordion-collapse-icon'], {
              [styles['highlight-icon']]: highlightActive,
            })}
          ></span>
        }
        activeIndex={activeIndexState}
        multiple={multiple}
        onTabChange={(event) => {
          if (!preventTabsToggle) {
            setActiveIndexState(event.index as number);
            onTabChange && onTabChange(event);
          }
        }}
      >
        {tabs.map((tab, index) => (
          <AccordionTab
            key={index}
            className={classNames(styles['accordion-tab'], {
              [styles['accordion-tab-active']]: activeIndexState === index,
              [styles['accordion-tab-prevent-toggle']]: preventTabsToggle,
            })}
            header={
              <div className={styles['accordion-tab-header']}>
                <span>{tab.header || (tabHeaderPrefix ? `${tabHeaderPrefix} ${index + 1}` : '')}</span>
                {allowToDelete && !(shouldPreserveFirstTab && tabs.length === 1) && (
                  <div
                    data-testid="delete-tab-icon"
                    className={classNames(styles['trash-icon'], { [styles['disabled']]: isDeleteDisabled })}
                    onClick={(event) => {
                      event.stopPropagation();
                      deleteTab(index);
                    }}
                  ></div>
                )}
              </div>
            }
          >
            {tab.body}
          </AccordionTab>
        ))}
      </AccordionsGroup>
    </div>
  );
};
